import type { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import {
  type AppPropsWithFeatureFlags,
  withFeatureFlags,
} from '@jusbrasil-web/shared-feature-flags'
import type { GetServerSidePropsContext } from 'next'

import {
  type SignupCpfFieldsFlags,
  type SignupCpfFieldsVariant,
  isSignupCpfFieldsVariant,
  SIGNUP_CPF_FIELDS_FLAG_KEY,
  SIGNUP_CPF_FIELDS_FLAGS,
} from './flag'

const FORCE_QUERY_PARAM = 'force_signup_cpf_fields'

function readForcedVariant(ctx: GetServerSidePropsContext): SignupCpfFieldsVariant | null {
  if (process.env.NEXT_PUBLIC_ENV === 'production') return null
  const raw = ctx.query?.[FORCE_QUERY_PARAM]
  const value = Array.isArray(raw) ? raw[0] : raw
  return isSignupCpfFieldsVariant(value) ? value : null
}

export interface ResolveSignupCpfFieldsVariantInput {
  ctx: GetServerSidePropsContext
  apolloClient: ApolloClient<NormalizedCacheObject>
}

export interface ResolveSignupCpfFieldsVariantResult {
  forcedVariant: SignupCpfFieldsVariant | null
  inExperiment: boolean
  featureFlagsProps: AppPropsWithFeatureFlags<SignupCpfFieldsFlags>
}

/**
 * SSR half of the experiment: ships the resolved flag map to the client and
 * reports whether GrowthBook bucketed this device.
 *
 * It deliberately never calls getFeatureValue — that would fire
 * Experiment.Participate once per SSR request (reloads, crawlers, and the
 * cpf-not-verified bounce back to /cadastro/cpf all hit this page), including
 * for requests that never hydrate. The variant is read client-side instead, so
 * exposure lines up with a rendered form. See docs/analytics.md.
 */
export async function resolveSignupCpfFieldsVariant({
  ctx,
  apolloClient,
}: ResolveSignupCpfFieldsVariantInput): Promise<ResolveSignupCpfFieldsVariantResult> {
  const ff = await withFeatureFlags(ctx, apolloClient, SIGNUP_CPF_FIELDS_FLAGS)

  return {
    forcedVariant: readForcedVariant(ctx),
    inExperiment: ff.inExperiment(SIGNUP_CPF_FIELDS_FLAG_KEY),
    featureFlagsProps: ff.withFeatureFlagsProps(),
  }
}
